import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Shield, Zap, Target, TrendingUp } from 'lucide-react';

interface PortfolioSummaryProps {
  riskScore: number;
  diversificationScore: number;
  performanceRank: number;
  totalTransactions: number;
  winRate: number;
  bestPerformer: {
    symbol: string;
    percentage: number;
  };
  worstPerformer: {
    symbol: string;
    percentage: number;
  };
}

const PortfolioSummary: React.FC<PortfolioSummaryProps> = ({
  riskScore,
  diversificationScore,
  performanceRank,
  totalTransactions,
  winRate,
  bestPerformer,
  worstPerformer,
}) => {
  const getRiskColor = (score: number) => {
    if (score <= 30) return 'text-green-400';
    if (score <= 70) return 'text-amber-400';
    return 'text-red-400';
  };

  const getRiskLabel = (score: number) => {
    if (score <= 30) return 'Low Risk';
    if (score <= 70) return 'Medium Risk';
    return 'High Risk';
  };

  const ScoreCard = ({ title, score, maxScore, icon: Icon, color }: any) => (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-zinc-400">{title}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div className="space-y-2">
        <div className="text-2xl font-bold text-white">
          {score}/{maxScore}
        </div>
        <div className="w-full bg-zinc-700 rounded-full h-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(score / maxScore) * 100}%` }}
            transition={{ duration: 1, delay: 0.5 }}
            className={`h-2 rounded-full ${color.replace('text-', 'bg-')}`}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6">
      <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
        <Activity className="w-5 h-5 text-violet-400" />
        Portfolio Health
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <ScoreCard
          title="Risk Score"
          score={riskScore}
          maxScore={100}
          icon={Shield}
          color={getRiskColor(riskScore)}
        />
        <ScoreCard
          title="Diversification"
          score={diversificationScore}
          maxScore={100}
          icon={Target}
          color="text-purple-400"
        />
        <ScoreCard
          title="Performance Rank"
          score={performanceRank}
          maxScore={100}
          icon={TrendingUp}
          color="text-blue-400"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-400">Best Performer</span>
            <Zap className="w-4 h-4 text-green-400" />
          </div>
          <div className="text-lg font-medium text-white">{bestPerformer.symbol}</div>
          <div className="text-2xl font-bold text-green-400">
            +{bestPerformer.percentage.toFixed(2)}%
          </div>
        </div>

        <div className="bg-zinc-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-400">Worst Performer</span>
            <Zap className="w-4 h-4 text-red-400 rotate-180" />
          </div>
          <div className="text-lg font-medium text-white">{worstPerformer.symbol}</div>
          <div className="text-2xl font-bold text-red-400">
            {worstPerformer.percentage.toFixed(2)}%
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between bg-zinc-800/30 rounded-lg p-4">
        <div>
          <div className="text-sm text-zinc-400">Win Rate</div>
          <div className="text-xl font-bold text-white">{winRate.toFixed(1)}%</div>
        </div>
        <div className="text-right">
          <div className="text-sm text-zinc-400">Total Transactions</div>
          <div className="text-xl font-bold text-white">{totalTransactions}</div>
        </div>
        <div className="text-right">
          <div className="text-sm text-zinc-400">Risk Level</div>
          <div className={`text-xl font-bold ${getRiskColor(riskScore)}`}>
            {getRiskLabel(riskScore)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSummary;